import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { OpcoUserAccountDetails } from './ui/OpcoUserAccountDetails';
import { UserPersonalDetails } from '@/bundle/shared/components/UserDetails/UserPersonalDetails';
import { useUser } from '@/context/userContext';
import { useState } from 'react';
import { Drawer } from '@/components/Drawer/Drawer';
import { EditOpcoPersonalDetailsForm } from '@/bundle/_Opco/_OpcoUsersPage/OpcoUserDetailsPage/ui/EditOpcoPersonalDetailsForm';
import { isBlockedOpcoUserStatus } from '@/bundle/_Opco/_OpcoUsersPage/helpers/opcoUserHelpers';
import { Wrapper } from '@/bundle/_Opco/_OpcoUsersPage/OpcoUserDetailsPage/styles';
import { getOpcoUsersPath } from '../path/path';
import { Box } from '@/components/Box/Box';
import { ProfileCookiesPreferences } from '@/bundle/shared/components/ProfileCookiesPreferences/ProfileCookiesPreferences';
import { loadOpcoUser } from '@/bundle/_Opco/_OpcoUsersPage/OpcoUserDetailsPage/api/api';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

export const OpcoUserDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user: currentUser } = useUser();
  const { navigateBack } = useStatefulNavigation();

  const [openForm, setOpenForm] = useState(false);

  const { data } = useQuery({
    queryKey: ['load_opco_user', id],
    queryFn: () => loadOpcoUser(id),
  });

  const opcoUser = data?.body;
  const isBlockedOpco = isBlockedOpcoUserStatus(opcoUser?.user?.status);

  const isEditableForm = currentUser?.id === id && !isBlockedOpco;
  const isOwnAccount = currentUser?.id === opcoUser?.user?.id;

  const personalDetails = {
    first_name: opcoUser?.user?.first_name,
    middle_name: opcoUser?.user?.middle_name,
    last_name: opcoUser?.user?.last_name,
  };

  const refetchUserInfo = () => {
    queryClient.invalidateQueries({ queryKey: ['load_opco_user'] });
  };

  const closeEditForm = () => setOpenForm(false);

  return (
    <ContentLayout
      title='OpCo user details'
      breadcrumbs='OpCo Users > User Details'
      backButton='Back to Opco Users'
      onBack={() => navigateBack(getOpcoUsersPath())}
    >
      <PageBody>
        {opcoUser && (
          <Box display='flex' flexDirection='column' rowGap='42px'>
            <Wrapper>
              <OpcoUserAccountDetails userDetails={opcoUser} onSuccess={refetchUserInfo} isOwnAccount={isOwnAccount} />
              <UserPersonalDetails
                onEditOpen={() => setOpenForm(true)}
                personalDetails={personalDetails}
                isEdit={isEditableForm}
              />
            </Wrapper>
            {isOwnAccount && <ProfileCookiesPreferences />}
          </Box>
        )}

        <Drawer
          isOpen={openForm}
          header='Edit Personal Info'
          subHeader='Please edit your information.'
          onClose={closeEditForm}
        >
          <EditOpcoPersonalDetailsForm
            personalDetails={personalDetails}
            onSuccess={refetchUserInfo}
            onClose={closeEditForm}
          />
        </Drawer>
      </PageBody>
    </ContentLayout>
  );
};
